// generic API fetching
/* eslint-disable @typescript-eslint/no-explicit-any */

import { fetchWithTimeout, ObjectWithStringKeys } from './fetchWithTimeout';

const BASE_URL = '/api/';

type Options = ObjectWithStringKeys;
const handleResponse = async (r: Response) => {
  try {
    if (r.ok) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return await r.json();
    }

    throw new Error(`API Error: ${r?.statusText}`);
  } catch (e: any) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const payload = await r.json().catch(() => null);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const msg = payload?.message ?? e?.message;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    throw new Error(msg);
  }
};

const defaultOptions: {
  credentials: RequestCredentials | undefined;
  headers: { [key: string]: string };
} = {
  credentials: 'same-origin',
  headers: {
    'Content-Type': 'application/json',
  },
};
const defaultOptionsForPostFiles: {
  credentials: RequestCredentials | undefined;
  headers: { [key: string]: string };
} = {
  credentials: 'same-origin',
  headers: {},
};

export function get(endpoint: string, options: Options = {}) {
  return fetchWithTimeout(`${BASE_URL}${endpoint}`, {
    ...defaultOptions,
    ...options,
    ...{
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      headers: {
        ...defaultOptions.headers,
        ...options.headers,
        Authorization: `Bearer ${
          localStorage.getItem('access_token')?.toString() || ''
        }`,
      },
    },
    method: 'GET',
  }).then(handleResponse);
}

export function put(endpoint: string, body: any, options: Options = {}) {
  return fetchWithTimeout(`${BASE_URL}${endpoint}`, {
    method: 'PUT',
    body: JSON.stringify(body),
    ...defaultOptions,
    ...{
      headers: {
        ...defaultOptions.headers,
        Authorization: `Bearer ${
          localStorage.getItem('access_token')?.toString() || ''
        }`,
      },
    },
    ...options,
  }).then(handleResponse);
}

export function patch(endpoint: string, body: any, options: Options = {}) {
  return fetchWithTimeout(`${BASE_URL}${endpoint}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
    ...defaultOptions,
    ...{
      headers: {
        ...defaultOptions.headers,
        Authorization: `Bearer ${
          localStorage.getItem('access_token')?.toString() || ''
        }`,
      },
    },
    ...options,
  }).then(handleResponse);
}

export function post(endpoint: string, body: any, options: Options = {}) {
  return fetchWithTimeout(`${BASE_URL}${endpoint}`, {
    method: 'POST',
    body: JSON.stringify(body),
    ...defaultOptions,
    ...{
      headers: {
        ...defaultOptions.headers,
        Authorization: `Bearer ${
          localStorage.getItem('access_token')?.toString() || ''
        }`,
      },
    },
    ...options,
  }).then(handleResponse);
}

export function postFiles(
  endpoint: string,
  body: FormData,
  options: Options = {}
) {
  return fetchWithTimeout(`${BASE_URL}${endpoint}`, {
    method: 'POST',
    body,
    ...defaultOptionsForPostFiles,
    ...{
      headers: {
        ...defaultOptionsForPostFiles.headers,
        // 'Content-Type': 'multipart/form-data', // Sending this will generate a 'boundary' error. We let the browser decide the content type in this case-
        Authorization: `Bearer ${
          localStorage.getItem('access_token')?.toString() || ''
        }`,
      },
    },
    ...options,
  });
}

export function fetchDelete(
  endpoint: string,
  body: any = {},
  options: Options = {}
) {
  return fetchWithTimeout(`${BASE_URL}${endpoint}`, {
    method: 'DELETE',
    body: JSON.stringify(body),
    ...defaultOptions,
    ...options,
    ...{
      headers: {
        ...defaultOptions.headers,
        Authorization: `Bearer ${
          localStorage.getItem('access_token')?.toString() || ''
        }`,
      },
    },
  }).then(handleResponse);
}
